import {io, Socket} from "socket.io-client";
import {createEventBus, EventBus} from "./core/EventManager";
import {
  createMediasoupManager,
  MediasoupManagerHandle,
} from "./core/MediasoupManager";
import {
  createSpatialAudioManager,
  SpatialAudioManagerHandle,
  SpatialAudioOptions,
} from "./channels/spatial/SpatialAudioChannel";
import {
  createScreenShareLiveManager,
  ScreenShareLiveManagerHandle,
} from "./screen-share-live-in-space";
import {
  SpaceLiveBroadcastAvailableEvent,
  SpaceLiveBroadcastStatusResponse,
  SpaceLiveBroadcastStoppedEvent,
  SpaceLiveBroadcastResult,
  Direction,
  MediaState,
  OdysseyEvent,
  Participant,
  ParticipantsSnapshotEvent,
  Position,
  RoomJoinedData,
} from "./types";
import {debugLog} from "./utils/debug";
import {createRoomActions, RoomState} from "./core/room";
import {createTrackActions} from "./producers/tracks";
import {registerSocketListeners} from "./events/listeners";
import {createChatManager, ChatHandle, ChatMessage} from "./chat";

// Re-export space live broadcast types for consumers
export type {
  SpaceLiveBroadcastAvailableEvent,
  SpaceLiveBroadcastStatusResponse,
  SpaceLiveBroadcastStoppedEvent,
  SpaceLiveBroadcastResult,
};

// ─── Public handle interface ─────────────────────────────────────────────────────────

export interface OdysseySpatialCommsHandle {
  room: {id: string; participants: Map<string, Participant>} | null;
  on(event: OdysseyEvent, listener: (...args: any[]) => void): void;
  off(event: OdysseyEvent, listener: (...args: any[]) => void): void;
  initializeMLNoiseSuppression(
    modelPath: string,
    workletUrl?: string
  ): Promise<void>;
  joinRoom(data: {
    roomId: string;
    userId: string;
    deviceId: string;
    participantId?: string;
    position: Position;
    direction: Direction;
    bodyHeight?: string;
    bodyShape?: string;
    userName?: string;
    userEmail?: string;
  }): Promise<Participant>;
  leaveRoom(): void;
  resumeAudio(): Promise<void>;
  getAudioContextState(): AudioContextState;
  enhanceOutgoingAudioTrack(track: MediaStreamTrack): Promise<MediaStreamTrack>;
  isMLNoiseSuppressionActive(): boolean;
  getNoiseSuppressionMode(): "ml" | "audioworklet" | "none";
  setMasterMuted(muted: boolean): void;
  getMasterMuted(): boolean;
  produceTrack(
    track: MediaStreamTrack,
    appData?: {isScreenshare?: boolean}
  ): Promise<any>;
  recreateProducers(): Promise<void>;
  updatePosition(
    position: Position,
    direction: Direction,
    spatialData?: {
      cameraDistance?: number;
      screenPos?: {x: number; y: number} | null;
      rot?: {x: number; y: number; z: number};
      pan?: number;
      dxLocal?: number;
    }
  ): void;
  updateMediaState(mediaState: MediaState): void;
  stopScreenShare(): Promise<void>;
  stopVideoProducer(): Promise<void>;
  startSpaceLiveBroadcast(): Promise<SpaceLiveBroadcastResult>;
  stopSpaceLiveBroadcast(): Promise<void>;
  getSpaceLiveBroadcastStatus(
    callback: (info: {
      isActive: boolean;
      producerId?: string;
      producerUserId?: string;
      userName?: string;
    }) => void
  ): void;
  readonly isSpaceLiveBroadcasting: boolean;
  setListenerPosition(
    position: Position,
    orientation: {
      forwardX: number;
      forwardY: number;
      forwardZ: number;
      upX: number;
      upY: number;
      upZ: number;
    }
  ): void;
  setListenerFromLSD(
    listenerPos: Position,
    cameraPos: Position,
    lookAtPos: Position,
    rot?: {x: number; y: number; z: number}
  ): void;
  chat: {
    init(data: {
      userId: string;
      userName?: string;
      channelId: string;
      channelName?: string;
    }): Promise<void>;
    sendMessage(message: ChatMessage): Promise<any>;
    sendNotification(data: {
      text: string;
      participants: string[];
      sender: {deviceId: string; name?: string; img?: string};
    }): Promise<any>;
    leave(): Promise<void>;
    onMessage(callback: (message: any) => void): void;
  };
  sendHuddleInvite(toParticipantId: string): Promise<{
    success?: boolean;
    inviteId?: string;
    huddleId?: string;
    error?: string;
  }>;
  acceptHuddleInvite(inviteId: string): Promise<{
    success?: boolean;
    huddleId?: string;
    participants?: string[];
    error?: string;
  }>;
  rejectHuddleInvite(
    inviteId: string
  ): Promise<{success?: boolean; error?: string}>;
  joinHuddle(): Promise<{
    success?: boolean;
    channelId?: string;
    error?: string;
  }>;
  leaveHuddle(): Promise<{
    success?: boolean;
    channelId?: string;
    error?: string;
  }>;
  getParticipantChannel(participantId: string): Promise<string>;
  muteParticipant(
    participantId: string
  ): Promise<{success?: boolean; error?: string}>;
  getCurrentChannel(): string;
}

// ─── Server URL (baked in at build time via ODYSSEY_SERVER_URL env var) ────────
declare const process: {env: {ODYSSEY_SERVER_URL: string}};
const ODYSSEY_SERVER_URL: string = process.env.ODYSSEY_SERVER_URL;

// ─── Factory function ─────────────────────────────────────────────────────────

/**
 * Create an Odyssey Spatial Comms instance (browser-only SDK).
 * @param apiKey        SDK API key issued by Odyssey (from payment service)
 * @param userToken     JWT access token for the authenticated user (from SSO)
 * @param spatialOptions  Optional spatial audio configuration
 */
export function createOdysseySpatialComms(
  apiKey: string,
  userToken: string,
  spatialOptions?: SpatialAudioOptions
): OdysseySpatialCommsHandle {
  console.log(
    `[OdysseySDK] 🔗 Server URL: ${ODYSSEY_SERVER_URL || "(empty — URL not baked in!)"}`
  );
  if (!ODYSSEY_SERVER_URL) {
    throw new Error(
      "[OdysseySDK] Server URL not configured. This SDK build is missing ODYSSEY_SERVER_URL."
    );
  }
  if (!apiKey || typeof apiKey !== "string" || !apiKey.trim()) {
    throw new Error("[OdysseySDK] A valid apiKey is required.");
  }
  // if (!userToken || typeof userToken !== "string" || !userToken.trim()) {
  //   throw new Error("[OdysseySDK] A valid userToken is required.");
  // }
  // ── Closure state ────────────────────────────────────────────────────────
  const bus = createEventBus();
  // Pass apiKey + userToken in handshake.auth — validated on every connect/reconnect.
  const socket: Socket = io(ODYSSEY_SERVER_URL, {
    transports: ["websocket"],
    auth: {apiKey, token: userToken},
  });

  let sessionStart: number | null = null;

  socket.on("connect", () => {
    sessionStart = Date.now();
    debugLog("Connection", `✅ Connected to server socketId=${socket.id}`);
  });
  socket.on("connect_error", (err) =>
    debugLog("Connection", `❌ Connection error: ${err.message}`)
  );
  socket.on("disconnect", (reason) => {
    if (sessionStart !== null) {
      const secs = Math.floor((Date.now() - sessionStart) / 1000);
      const mins = Math.floor(secs / 60);
      const remainSecs = secs % 60;
      const duration = mins > 0 ? `${mins}m ${remainSecs}s` : `${remainSecs}s`;
      console.log(
        `[OdysseySDK] 📊 Session ended — duration: ${duration} (${secs}s raw)`
      );
      sessionStart = null;
    }
    debugLog("Connection", `🔌 Disconnected reason=${reason}`);
  });
  const mediasoupManager = createMediasoupManager(socket);
  const spatialAudioManager = createSpatialAudioManager(spatialOptions);
  const screenShareLiveManager = createScreenShareLiveManager(socket);

  // Shared mutable state — passed by reference to all sub-modules
  const state: RoomState = {room: null, localParticipant: null};

  function emitEvent(event: OdysseyEvent, ...args: any[]): void {
    bus.emit(event, ...args);
  }

  bus.emit("connected" as OdysseyEvent);

  // ── Wire logic modules ─────────────────────────────────────────────────
  const chatManager = createChatManager({
    serverUrl: ODYSSEY_SERVER_URL,
  });

  const {
    joinRoom: _joinRoom,
    leaveRoom: _leaveRoom,
    updatePosition,
    updateMediaState,
  } = createRoomActions({socket, bus, mediasoupManager, state, emitEvent});

  const {produceTrack, recreateProducers, stopScreenShare, stopVideoProducer} =
    createTrackActions({socket, state, mediasoupManager, updateMediaState});

  registerSocketListeners({
    socket,
    state,
    mediasoupManager,
    spatialAudioManager,
    emitEvent,
  });

  // ── Chat-aware room wrappers ─────────────────────────────────────────────

  async function joinRoom(data: Parameters<typeof _joinRoom>[0]) {
    return _joinRoom(data);
  }

  function leaveRoom() {
    chatManager.leave().catch(() => {});
    _leaveRoom();
  }

  // ── Public methods ───────────────────────────────────────────────────────

  async function initializeMLNoiseSuppression(
    modelPath: string,
    workletUrl?: string
  ): Promise<void> {
    try {
      await spatialAudioManager.initializeMLNoiseSuppression(
        modelPath,
        workletUrl
      );
    } catch (error) {
      // Graceful degradation - AudioWorklet will be used as fallback
    }
  }

  async function resumeAudio(): Promise<void> {
    await spatialAudioManager.resumeAudioContext();
  }

  function getAudioContextState(): AudioContextState {
    return spatialAudioManager.getAudioContextState();
  }

  async function enhanceOutgoingAudioTrack(
    track: MediaStreamTrack
  ): Promise<MediaStreamTrack> {
    return spatialAudioManager.enhanceOutgoingAudioTrack(track);
  }

  function isMLNoiseSuppressionActive(): boolean {
    return spatialAudioManager.getNoiseSuppressionMode() === "ml";
  }

  function getNoiseSuppressionMode(): "ml" | "audioworklet" | "none" {
    return spatialAudioManager.getNoiseSuppressionMode();
  }

  function setMasterMuted(muted: boolean): void {
    spatialAudioManager.setMasterMuted(muted);
  }

  function getMasterMuted(): boolean {
    return spatialAudioManager.getMasterMuted();
  }

  async function startSpaceLiveBroadcast(): Promise<SpaceLiveBroadcastResult> {
    if (!state.localParticipant || !state.room) {
      throw new Error("Must be in a room to start space live broadcast");
    }
    return screenShareLiveManager.startBroadcast(
      state.room,
      state.localParticipant,
      ODYSSEY_SERVER_URL
    );
  }

  async function stopSpaceLiveBroadcast(): Promise<void> {
    return screenShareLiveManager.stopBroadcast(
      state.room,
      state.localParticipant?.participantId
    );
  }

  function getSpaceLiveBroadcastStatus(
    callback: (info: {
      isActive: boolean;
      producerId?: string;
      producerUserId?: string;
      userName?: string;
    }) => void
  ): void {
    screenShareLiveManager.getStatus(state.room?.id || null, callback);
  }

  function setListenerPosition(
    position: Position,
    orientation: {
      forwardX: number;
      forwardY: number;
      forwardZ: number;
      upX: number;
      upY: number;
      upZ: number;
    }
  ): void {
    spatialAudioManager.setListenerPosition(position, orientation);
  }

  function setListenerFromLSD(
    listenerPos: Position,
    cameraPos: Position,
    lookAtPos: Position,
    rot?: {x: number; y: number; z: number}
  ): void {
    spatialAudioManager.setListenerFromLSD(
      listenerPos,
      cameraPos,
      lookAtPos,
      rot
    );
  }

  async function sendHuddleInvite(toParticipantId: string): Promise<{
    success?: boolean;
    inviteId?: string;
    huddleId?: string;
    error?: string;
  }> {
    if (!state.localParticipant || !state.room)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "send-huddle-invite",
        {
          fromParticipantId: state.localParticipant!.participantId,
          toParticipantId,
          roomId: state.room!.id,
        },
        (response: {
          success?: boolean;
          inviteId?: string;
          huddleId?: string;
          error?: string;
        }) => resolve(response)
      );
    });
  }

  async function acceptHuddleInvite(inviteId: string): Promise<{
    success?: boolean;
    huddleId?: string;
    participants?: string[];
    error?: string;
  }> {
    if (!state.localParticipant)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "accept-huddle-invite",
        {inviteId, participantId: state.localParticipant!.participantId},
        (response: {
          success?: boolean;
          huddleId?: string;
          participants?: string[];
          error?: string;
        }) => resolve(response)
      );
    });
  }

  async function rejectHuddleInvite(
    inviteId: string
  ): Promise<{success?: boolean; error?: string}> {
    if (!state.localParticipant)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "reject-huddle-invite",
        {inviteId, participantId: state.localParticipant!.participantId},
        (response: {success?: boolean; error?: string}) => resolve(response)
      );
    });
  }

  async function joinHuddle(): Promise<{
    success?: boolean;
    channelId?: string;
    error?: string;
  }> {
    if (!state.localParticipant || !state.room)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "join-huddle",
        {
          participantId: state.localParticipant!.participantId,
          roomId: state.room!.id,
        },
        (response: {success?: boolean; channelId?: string; error?: string}) => {
          if (response.success && state.localParticipant) {
            state.localParticipant!.currentChannel = response.channelId;
          }
          resolve(response);
        }
      );
    });
  }

  async function leaveHuddle(): Promise<{
    success?: boolean;
    channelId?: string;
    error?: string;
  }> {
    if (!state.localParticipant || !state.room)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "leave-huddle",
        {
          participantId: state.localParticipant!.participantId,
          roomId: state.room!.id,
        },
        (response: {success?: boolean; channelId?: string; error?: string}) => {
          if (response.success && state.localParticipant) {
            state.localParticipant!.currentChannel = response.channelId;
          }
          resolve(response);
        }
      );
    });
  }

  async function getParticipantChannel(participantId: string): Promise<string> {
    return new Promise((resolve) => {
      socket.emit(
        "get-participant-channel",
        {participantId},
        (response: {success?: boolean; channelId?: string; error?: string}) => {
          resolve(response.channelId || "spatial");
        }
      );
    });
  }

  async function muteParticipant(
    participantId: string
  ): Promise<{success?: boolean; error?: string}> {
    if (!state.localParticipant || !state.room)
      return {success: false, error: "Not in a room"};
    return new Promise((resolve) => {
      socket.emit(
        "mute-participant",
        {
          targetParticipantId: participantId,
          roomId: state.room!.id,
          requesterId: state.localParticipant!.participantId,
        },
        (response: {success?: boolean; error?: string}) => resolve(response)
      );
    });
  }

  function getCurrentChannel(): string {
    return state.localParticipant?.currentChannel || "spatial";
  }

  // ── Return handle ─────────────────────────────────────────────────────────
  return {
    get room() {
      return state.room;
    },
    on: (event, listener) => {
      bus.on(event, listener);
    },
    off: (event, listener) => {
      bus.off(event, listener);
    },
    initializeMLNoiseSuppression,
    joinRoom,
    leaveRoom,
    resumeAudio,
    getAudioContextState,
    enhanceOutgoingAudioTrack,
    isMLNoiseSuppressionActive,
    getNoiseSuppressionMode,
    setMasterMuted,
    getMasterMuted,
    produceTrack,
    recreateProducers,
    updatePosition,
    updateMediaState,
    stopScreenShare,
    stopVideoProducer,
    startSpaceLiveBroadcast,
    stopSpaceLiveBroadcast,
    getSpaceLiveBroadcastStatus,
    get isSpaceLiveBroadcasting() {
      return screenShareLiveManager.isActive;
    },
    setListenerPosition,
    setListenerFromLSD,
    sendHuddleInvite,
    acceptHuddleInvite,
    rejectHuddleInvite,
    joinHuddle,
    leaveHuddle,
    getParticipantChannel,
    muteParticipant,
    getCurrentChannel,
    chat: {
      init: (data: {
        userId: string;
        userName?: string;
        channelId: string;
        channelName?: string;
      }) => chatManager.init(data),
      sendMessage: (message: ChatMessage) => chatManager.sendMessage(message),
      sendNotification: (data: {
        text: string;
        participants: string[];
        sender: {deviceId: string; name?: string; img?: string};
      }) => chatManager.sendNotification(data),
      leave: () => chatManager.leave(),
      onMessage: (callback: (message: any) => void) => {
        (chatManager as any).onMessage = callback;
      },
    },
  };
}

export type {
  ChatMessage,
  Direction,
  MediaState,
  OdysseyEvent,
  Participant,
  Position,
  RoomJoinedData,
  SpatialAudioOptions,
};
export type {OdysseyEvent as OdysseySDKEvent};

// ─── Namespace singleton — the standard public API ───────────────────────────
//
//   import SpatialCommsSDK from 'odyssey-spatial-comms';
//   const client = SpatialCommsSDK.create('https://your-server.com');
//   client.on('room-joined', handler);
//   await client.joinRoom({ ... });
//
// ─────────────────────────────────────────────────────────────────────────────

const SpatialCommsSDK = {
  /**
   * Create a new SDK client instance.
   * @param apiKey        SDK API key issued by Odyssey (from payment service)
   * @param userToken     JWT access token for the authenticated user (from SSO)
   * @param spatialOptions  Optional spatial audio configuration
   */
  create(
    apiKey: string,
    userToken: string,
    spatialOptions?: SpatialAudioOptions
  ): OdysseySpatialCommsHandle {
    return createOdysseySpatialComms(apiKey, userToken, spatialOptions);
  },
} as const;

export {SpatialCommsSDK};
